import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import * as yup from 'yup';

import { nameIdNonRequiredSchema } from '~constants/regexConst';
import { Account, AccountBasic } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import {
  ItemColorLabelValue,
  Rate,
  RateFlatCommissionInputs,
  RateFlatRateInputs,
  RatePerUnitInputs,
  RateTimeInputs,
} from '~hooks/useRates/models';
import { User } from '~hooks/useUsers/models';
import {
  addOnAssignmentSchema,
  testUniqueAddOnAssignments,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { rateTypeLabelColor } from '~pages/Settings/RatesManagement/rateUtils';
import { Nullable } from '~types/Nullable';
import isEnumValue from '~utils/isEnumValue';

const timeRateSchema = yup.object().shape({
  rate: yup
    .number()
    .required(`${t('form_validation_errors.required_simple')}`)
    .typeError(t('form_validation_errors.should_be_number_simple') || ''),
  minimum: yup.string().required(`${t('form_validation_errors.required_simple')}`),
  increment: yup.string().required(`${t('form_validation_errors.required_simple')}`),
});
const generalSchema = yup.object().shape({
  rate: yup
    .number()
    .required(`${t('form_validation_errors.required_simple')}`)
    .typeError(t('form_validation_errors.should_be_number_simple') || ''),
  minimum: yup.string().required(`${t('form_validation_errors.required_simple')}`),
  increment: yup.string().notRequired(),
});
const commissionRateSchema = yup.object().shape({
  rate: yup
    .number()
    .max(100, `${t('form_validation_errors.max', { field: '100' })}`)
    .min(
      0,
      `${t('form_validation_errors.min', {
        field: t('administration.rates.commission_rate'),
        count: 0,
      })}`,
    )
    .required(`${t('form_validation_errors.required_simple')}`)
    .typeError(t('form_validation_errors.should_be_number_simple') || ''),
});
const flatCommissionRateSchema = yup.object().shape({
  rate: yup
    .number()
    .required(`${t('form_validation_errors.required_simple')}`)
    .typeError(t('form_validation_errors.should_be_number_simple') || ''),
});
const flatRateSchema = yup.object().shape({
  rate: yup
    .number()
    .required(`${t('form_validation_errors.required_simple')}`)
    .typeError(t('form_validation_errors.should_be_number_simple') || ''),
});

const rateTypedSchemas = {
  [RateType.RATE_PER_DAY]: generalSchema,
  [RateType.RATE_PER_HOUR]: timeRateSchema,
  [RateType.RATE_COMMISSION]: commissionRateSchema,
  [RateType.RATE_PER_TON]: generalSchema,
  [RateType.RATE_PER_TONNE]: generalSchema,
  [RateType.RATE_PER_LOAD]: generalSchema,
  [RateType.RATE_PER_YARD]: generalSchema,
  [RateType.RATE_PER_BUSHEL]: generalSchema,
  [RateType.RATE_PER_POUND]: generalSchema,
  [RateType.RATE_PER_EACH]: generalSchema,
  [RateType.RATE_FLAT_COMMISSION]: flatCommissionRateSchema,
  [RateType.RATE_FLAT_RATE]: flatRateSchema,
} satisfies Record<RateType, yup.AnyObjectSchema>;
export const rateFormValidationSchema = yup.object().shape({
  id: yup.string().notRequired(),
  name: yup.string().required(`${t('form_validation_errors.required_simple')}`),
  account: nameIdNonRequiredSchema.notRequired(),
  projects: yup.array().of(nameIdNonRequiredSchema).notRequired(),
  services: yup.array().of(nameIdNonRequiredSchema).notRequired(),
  equipment: yup.array().of(nameIdNonRequiredSchema).notRequired(),
  equipmentType: nameIdNonRequiredSchema.notRequired(),
  serviceClass: nameIdNonRequiredSchema.notRequired(),
  company: nameIdNonRequiredSchema,
  isFuelSurcharge: yup.boolean(),
  type: yup
    .object()
    .shape({
      value: yup.string().required(),
      label: yup.string().required(),
      color: yup.string(),
    })
    .required(`${t('form_validation_errors.required_simple')}`),
  isPrimary: yup.boolean(),
  rateInputs: yup.object().when('type', ([type]) => {
    const rateType: string = type.value;

    return isEnumValue(RateType, rateType) ? rateTypedSchemas[rateType] : yup.object();
  }) as yup.AnyObjectSchema,
  driver: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      firstName: yup.string().notRequired(),
      lastName: yup.string().notRequired(),
    })
    .notRequired(),
  automaticAddOns: yup
    .array()
    .of(addOnAssignmentSchema)
    .required()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
  ownerType: yup.object().shape({
    label: yup.string().required(),
    value: yup.string().oneOf(Object.values(RateOwnerType)).required(),
  }),
});
const setDefaultRateType = (defaultRate: Rate) => {
  if (defaultRate.rateInputs instanceof RateTimeInputs) {
    return {
      rate: defaultRate?.rateInputs?.timeRate || '',
      minimum: defaultRate?.rateInputs?.timeMinimumMinutes || '',
      increment: defaultRate?.rateInputs?.timeIncrementMinutes || '',
    };
  } else if (defaultRate.rateInputs instanceof RatePerUnitInputs) {
    return {
      rate: defaultRate?.rateInputs?.perUnitRate || '',
      minimum: defaultRate?.rateInputs?.perUnitMinimum || '',
      increment: defaultRate?.rateInputs?.perUnitIncrement || '',
    };
  } else if (defaultRate.rateInputs instanceof RateFlatCommissionInputs) {
    return {
      rate: defaultRate?.rateInputs?.commissionAmount || '',
    };
  } else if (defaultRate.rateInputs instanceof RateFlatRateInputs) {
    return {
      rate: defaultRate?.rateInputs?.flatAmount || '',
    };
  } else {
    return {
      rate: defaultRate?.rateInputs?.commissionPercentage || '',
    };
  }
};
export const setDefaultRateValues = (
  defaultRate: Rate,
  account?: Nullable<Account>,
  driver?: Nullable<User>,
  altCompany?: CompanyBasic,
  ownerType?: RateOwnerType,
) => {
  const accountData = defaultRate?.account?.id?.length
    ? defaultRate?.account
    : (account as AccountBasic);
  return {
    id: defaultRate?.id || '',
    name: defaultRate?.name || '',
    account: {
      name: accountData?.name || '',
      id: accountData?.id || '',
    },
    company: {
      legalName: defaultRate?.company?.legalName || altCompany?.legalName || '',
      id: defaultRate?.company?.id || altCompany?.id || '',
    },
    projects: (defaultRate?.projects ?? []).map((item) => ({
      name: item.name,
      id: item.id,
    })),
    services: (defaultRate?.services ?? []).map((item) => ({
      name: item.name,
      id: item.id,
    })),
    equipment: (defaultRate?.equipment ?? []).map((item) => ({
      name: item.name,
      id: item.id,
    })),
    equipmentType: defaultRate?.equipmentType
      ? { id: defaultRate.equipmentType.id, name: defaultRate.equipmentType.name }
      : null,
    serviceClass: defaultRate?.serviceClass
      ? { id: defaultRate.serviceClass.id, name: defaultRate.serviceClass.name }
      : null,
    isFuelSurcharge: defaultRate.isFuelSurcharge,
    isPrimary: defaultRate.isPrimary,
    type: defaultRate?.type?.value
      ? (rateTypeLabelColor(defaultRate.type?.value) as ItemColorLabelValue)
      : ({} as ItemColorLabelValue),

    rateInputs: defaultRate ? setDefaultRateType(defaultRate) : {},
    driver: driver
      ? {
          firstName: driver?.firstName || '',
          lastName: driver?.lastName || '',
          id: driver?.id || '',
        }
      : null,
    automaticAddOns: [],
    ownerType: ownerType
      ? {
          value: ownerType,
          label: t(
            `administration.rates.rate_owner_type_options.${ownerType.toLowerCase()}`,
          ),
        }
      : null,
  };
};

export type RatesFormSchemaInterface = yup.InferType<typeof rateFormValidationSchema>;
